import he from "he";
import { Controller, useForm } from "react-hook-form";
import { IPFilter } from "../core/api/organizations/types";
import { ITreeData } from "../ui-lib/components/Hierarchy/Tree";
import { Spacer } from "./Layout/Layout";
import { CustomDropdown } from "../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import { validateIPAddress } from "../core/helpers/validation";
import TextInput from "../ui-lib/components/Inputs/TextInput";
import useTranslations from "../core/i18n/useTranslations";
import PrimeModal from "../ui-lib/components/PrimeModal/PrimeModal";
import TextArea from "../ui-lib/components/Inputs/Textarea";
import Checkbox from "../ui-lib/components/Checkbox/Checkbox";

export const AddEditIPForm = ({
  defaultValues,
  parentId,
  isOpen,
  isLoading,
  withOrgFilter,
  onClose,
  onSubmit,
}: {
  defaultValues?: IPFilter;
  parentId: string;
  isOpen: boolean;
  isLoading: boolean;
  withOrgFilter: boolean;
  onClose: (reload?: boolean) => void;
  onSubmit: (values: IPFilter) => Promise<void>;
}) => {
  const t = useTranslations();
  const {
    control,
    trigger,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<IPFilter>({ defaultValues: { ...defaultValues, inherit: true } });
  return (
    <PrimeModal
      withFooter
      withHeader
      header={t(`Organizations:${defaultValues ? "edit" : "create"}_ip_title`)}
      isOpen={isOpen}
      onClose={() => onClose()}
      fixedWidth="848px"
      submitBtn={{
        text: t(
          defaultValues
            ? "Common:save_changes"
            : "AlarmReceptions:create_ip_address"
        ),
        onClick: handleSubmit(onSubmit),
      }}
      cancelBtn={{ text: t("Common:cancel"), onClick: () => onClose(false) }}
      loading={isLoading}
    >
      <p className="grey-600">
        {t(`Organizations:${defaultValues ? "edit" : "create"}_ip_description`)}
      </p>

      <Spacer size={32} />

      <form id="ip_address_from">
        <Controller
          name="startIP"
          control={control}
          rules={{
            required: true,
            validate: validateIPAddress,
          }}
          render={({ field }) => (
            <TextInput
              required
              inputRef={field.ref}
              label={t("AlarmReceptions:edit_ipfilter_label_startip")}
              placeholder={t("AlarmReceptions:edit_ipfilter_placeholder_endip")}
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors.startIP &&
                (field.value?.length === 0
                  ? t("Errors:input_field_required")
                  : t("AlarmReceptions:edit_ipfilter_ip_format"))
              }
            />
          )}
        />

        <Spacer size={16} />

        <Controller
          name="endIP"
          control={control}
          rules={{
            required: true,
            validate: validateIPAddress,
          }}
          render={({ field }) => (
            <TextInput
              required
              inputRef={field.ref}
              label={t("AlarmReceptions:advanced_settings_ipfilters_endip")}
              placeholder={t("AlarmReceptions:edit_ipfilter_placeholder_endip")}
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors.endIP &&
                (field.value?.length === 0
                  ? t("Errors:input_field_required")
                  : t("AlarmReceptions:edit_ipfilter_ip_format"))
              }
            />
          )}
        />

        {withOrgFilter && (
          <>
            <Spacer size={16} />

            <Controller
              name="belongsToId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CustomDropdown
                  required
                  selectionMode="single"
                  fullWidth
                  parentId={parentId}
                  label={t("Common:belongs_to")}
                  placeholder={t("Common:select_organisation")}
                  customerFilter={{ customers: field.value?.toString() }}
                  onSelectSignle={(selected?: ITreeData) => {
                    setValue(field.name, selected?.key as number);
                    trigger(field.name);
                  }}
                  validationError={
                    errors?.belongsToId && t("Errors:input_field_required")
                  }
                />
              )}
            />
          </>
        )}

        <Spacer size={16} />

        <Controller
          name="comment"
          control={control}
          rules={{
            validate: (input: string | undefined) =>
              !input || input.length < 2048,
          }}
          render={({ field }) => (
            <TextArea
              inputRef={field.ref}
              label={t(
                "AlarmReceptions:advanced_settings_addintegration_comment_label"
              )}
              placeholder={t(
                "AlarmReceptions:advanced_settings_addintegration_comment_placeholder"
              )}
              value={he.decode(field.value || "")}
              onChange={field.onChange}
              validationError={
                errors?.comment && t("Errors:ValidationHint_OrganizationInfo")
              }
            />
          )}
        />

        <Spacer size={16} />

        <Controller
          control={control}
          name="adminApi"
          render={({ field }) => (
            <Checkbox
              label={t("Common:admin")}
              checked={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </form>
    </PrimeModal>
  );
};
